import { taxSchema } from "./tax";
import { z } from "zod";

const unitTypeEnum = ["hour", "day", "service", "piece", "kg", "m", "sqm", "custom"] as const;

// Schema for creating items (client-side, without orgId and createdBy)
export const createItemSchema = z.object({
  name: z.string({ invalid_type_error: "Name must be a string" }).min(1, { message: "Name cannot be empty" }),
  description: z.string({ invalid_type_error: "Description must be a string" }).optional().nullable(),
  sku: z.string({ invalid_type_error: "SKU must be a string" }).optional().nullable(),
  unitType: z.enum(unitTypeEnum, { invalid_type_error: "Unit type must be a valid option" }).optional().nullable(),
  unitTypeValue: z.string({ invalid_type_error: "Unit type value must be a string" }).optional().nullable(),
  currency: z
    .string({ invalid_type_error: "Currency must be a string" })
    .length(3, { message: "Currency must be 3 characters" })
    .regex(/^[A-Z]{3}$/, { message: "Currency must be uppercase ISO code" }),
  netPrice: z.coerce.number().min(0, { message: "Net price must be positive" }),
  grossPrice: z.coerce.number().min(0, { message: "Gross price must be positive" }),
  isTaxInclusive: z.boolean({ invalid_type_error: "Tax inclusive must be a boolean" }),
  taxIds: z.array(z.string({ invalid_type_error: "Tax ID must be a string" })),

  // Profitability (never exposed to client)
  costPrice: z
    .number({ invalid_type_error: "Cost price must be a number" })
    .min(0, { message: "Cost price must be non-negative" })
    .optional()
    .nullable(),
  costCurrency: z
    .string({ invalid_type_error: "Cost currency must be a string" })
    .length(3, { message: "Cost currency must be 3 characters" })
    .regex(/^[A-Z]{3}$/, { message: "Cost currency must be uppercase ISO code" })
    .optional()
    .nullable(),

  active: z.boolean({ invalid_type_error: "Active must be a boolean" }),
});

// Schema for server-side item creation (includes orgId and createdBy)
export const serverCreateItemSchema = createItemSchema.extend({
  orgId: z.string({ invalid_type_error: "Organization ID must be a string" }),
  createdBy: z.string({ invalid_type_error: "Created by must be a string" }).optional().nullable(),
});

export const updateItemSchema = createItemSchema.partial().extend({
  id: z.string({ invalid_type_error: "ID must be a string" }),
});

export const getItemSchema = z.object({
  id: z.string({ invalid_type_error: "ID must be a string" }),
});

export const deleteItemSchema = z.object({
  id: z.string({ invalid_type_error: "ID must be a string" }),
});

// Unit type enum for filtering
export const unitTypeEnumSchema = z.enum(["hour", "day", "service", "piece", "kg", "m", "sqm", "custom"]);

// Filter params for listing items
export const getItemsParamsSchema = z.object({
  // Text search filters
  name: z.string().optional(),
  description: z.string().optional(),
  sku: z.string().optional(),
  // Option filters
  unitType: z
    .union([unitTypeEnumSchema, z.array(unitTypeEnumSchema)])
    .optional()
    .transform((val) => (val ? (Array.isArray(val) ? val : [val]) : undefined)),
  active: z.coerce.boolean().optional(),
  // Number range filters
  netPriceMin: z.coerce.number().optional(),
  netPriceMax: z.coerce.number().optional(),
  grossPriceMin: z.coerce.number().optional(),
  grossPriceMax: z.coerce.number().optional(),
});

export type ZodCreateItemSchema = z.infer<typeof createItemSchema>;
export type ZodUpdateItemSchema = z.infer<typeof updateItemSchema>;
export type ZodGetItemSchema = z.infer<typeof getItemSchema>;
export type ZodDeleteItemSchema = z.infer<typeof deleteItemSchema>;
export type ZodGetItemsParams = z.infer<typeof getItemsParamsSchema>;

export const itemSchema = z.object({
  id: z.string(),
  orgId: z.string(),
  createdBy: z.string().optional().nullable(),
  name: z.string(),
  description: z.string().optional().nullable(),
  sku: z.string().optional().nullable(),
  unitType: z.enum(unitTypeEnum),
  unitTypeValue: z.string().optional().nullable(),
  currency: z.string(),
  netPrice: z.number().min(0, { message: "Net price must be positive" }),
  grossPrice: z.number().min(0, { message: "Gross price must be positive" }),
  isTaxInclusive: z.boolean(),
  taxIds: z.array(z.string()),
  taxes: z.array(taxSchema).optional().nullable(),
  costPrice: z.number().optional().nullable(),
  costCurrency: z.string().optional().nullable(),
  active: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type ItemSchema = z.infer<typeof itemSchema>;

export const createItemSchemaDefaultValues: ZodCreateItemSchema = {
  name: "",
  unitType: "service",
  currency: "CHF",
  netPrice: 0,
  grossPrice: 0,
  isTaxInclusive: false,
  taxIds: [],
  active: true,
};
